import type React from 'react';
import {
  DASHBOARD_ENTITY_TYPES,
  DASHBOARD_TAB_LABEL,
  LEGACY_ANALYTICS_TYPE,
  asDashboardEntityType,
  canonicalTabKey,
} from '@components/DashboardShared';
import { DEFAULT_BRANDING_COMPANY_NAME } from '@config/config';
import LocalStorageKey from '@enums/LocalStorageKey';
import LocalStorageService from '@services/LocalStorageService';
import { getCompanyFromUrl } from '@utils/index';
import { create } from 'zustand';
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware';
import { SpendWindow, AdFiltersState } from '@customTypes/ad/AdFilters';
import { AdStatus } from '@enums/Ad';
import { getAppStorePersistKey } from '@stores/utils';
import { dropRetiredDashboards } from './retiredDashboards';
import { useRecentArtifactsStore } from '@stores/recentArtifactsStore';
import type { ArtifactType } from '@customTypes/artifact';

export type TabEntityType =
  | 'campaign'
  | 'adset'
  | 'ad'
  | 'report'
  | 'asset'
  | 'scheduledWork'
  | 'scheduledWorkExecution'
  | 'creativesDashboard'
  | 'analyticsDashboard'
  | 'leadAttribution'
  | 'galleryDashboard'
  | 'monitorsDashboard'
  | 'campaignTimeline';

/** Full-bleed dashboard tabs — derived from the canonical dashboard list so
 *  another dashboard is a one-place add (DashboardShared). */
const DASHBOARD_TAB_TYPES: ReadonlySet<TabEntityType> = new Set(
  DASHBOARD_ENTITY_TYPES,
);
export const isDashboardTab = (entityType?: TabEntityType): boolean =>
  !!entityType && DASHBOARD_TAB_TYPES.has(entityType);

/**
 * Home layout mode. 'ide' is the classic three-pane IDE (work tree · canvas ·
 * chat); 'agentic' is the Cursor-style agents-by-state layout. Persisted and
 * defaulting to 'ide' so the existing experience is byte-identical unless the
 * user opts in. Mirrors the client-only persisted precedent set by
 * chatbotStore.slackSimulation.
 */
export type LayoutMode = 'ide' | 'agentic';

/** What the center panel shows when no entity tab is focused. */
export type CenterView = 'home' | 'executive';

/** Sort key for ads within an adset (app-wide, synced across tabs). */
export type AdSortKey =
  | 'name-asc'
  | 'name-desc'
  | 'status'
  | 'spend-desc'
  | 'spend-asc'
  | 'date-desc'
  | 'date-asc';

/** Sort key for adsets within a campaign (app-wide, synced across tabs). */
export type AdsetSortKey = AdSortKey;

/** Metric grouping for the Meta campaign detail's tile pane (per-tab). */
export type MetricGroupKey = 'performance' | 'delivery' | 'app_promotion' | 'engagement';

export interface DrillDown {
  entityType: 'adset' | 'ad';
  entityId: string;
  entityName: string;
  parentAdsetId?: string;
  parentAdsetName?: string;
}

// The platforms whose adset/ad live as a DRILL-DOWN inside the campaign tab rather than as a
// tab of their own. A platform missing here still opens — it just opens its ad at the top
// level, which is why X and ChatGPT Ads belong in the set now that Leo can build on them.
const AD_PLATFORMS = new Set(['meta', 'google', 'linkedin', 'reddit', 'tiktok', 'bing', 'x', 'openai']);

export interface TabPane {
  label: string;
  key: string;
  entityType: TabEntityType;
  entityId: string;
  entityName: string;
  // VS Code-style preview tab: every open starts as the single preview tab,
  // replaced in place by the next open. A click anywhere on the tab (header
  // or content) promotes it to permanent via pinTab. Absent = permanent, so
  // previously persisted tabs are grandfathered in. Preview tabs are not
  // persisted across reloads.
  preview?: boolean;
  // Per-tab ad sorting preference (defaults to 'name-asc' if not set)
  adSort?: AdSortKey;
  // Per-tab metric grouping for the campaign detail pane (defaults to 'performance')
  metricGroup?: MetricGroupKey;
  // Additional metadata that might be useful
  metadata?: {
    status?: string;
    roas?: number | null;
    spend?: number | null;
    drillDown?: DrillDown | null;
    [key: string]: any;
  };
}

type AppActions = {
  setSidebarCollapsed: (collapsed: boolean) => void;
  setPageTitle: (pageTitle: string) => void;
  setCompanyName: (companyName: string) => void;
  getCompanyName: () => string;
  setCompanyDisplayName: (companyDisplayName: string) => void;
  setIsConnectionLoading: (isConnectionLoading: boolean) => void;
  setSurveyValues: (surveyValues: any) => void;
  setIsLeonardoOpen: (isLeonardoOpen: boolean) => void;
  setIsLeonardoNewOpen: (isLeonardoNewOpen: boolean) => void;
  setIsSubscriptionModalOpen: (isSubscriptionModalOpen: boolean) => void;
  openCheckout: () => void;
  setLeonardoInitialMessage: (message: string | null) => void;
  openLeonardoWithMessage: (message: string) => void;
  setMockChatMode: (enabled: boolean) => void;
  addTab: (tab: TabPane) => void;
  pinTab: (key: string) => void;
  updateTab: (key: string, updates: Partial<Pick<TabPane, 'label' | 'entityName' | 'metadata' | 'adSort' | 'metricGroup'>>) => void;
  removeTab: (key: string) => void;
  drillDownInTab: (tabKey: string, drillDown: DrillDown | null) => void;
  navigateToEntity: (opts: {
    entityType: TabEntityType;
    entityId: string;
    entityName: string;
    platform: string;
    parentCampaignId?: string;
    parentCampaignName?: string;
    parentAdsetId?: string;
    parentAdsetName?: string;
    metadata?: Record<string, any>;
  }) => void;
  clearAllTabs: () => void;
  resetStore: () => void;
  goHome: () => void;
  /** Show the staff-only Executive tab in the center (no entity tab focused). */
  goExecutive: () => void;
  setActiveTab: (key: string) => void;
  setSelectedTab: (key: string | null) => void;
  setReportsTreeExpandedKeys: (keys: React.Key[]) => void;
  setAssetTreeExpandedKeys: (keys: React.Key[]) => void;
  setScheduledWorkTreeExpandedKeys: (keys: React.Key[]) => void;
  collapseAllSidebarTrees: () => void;
  expandSidebarTree: (tree: 'reports' | 'assets' | 'scheduledWork', keys: React.Key[]) => void;
  // Drill-down sidebar navigation: an ordered list of node keys from the root
  // to the level currently shown (empty = root). Replaces the tree's
  // expanded-keys model for the new SidebarNav navigator.
  setSidebarNavPath: (path: string[]) => void;
  setMetricsWindow: (window: SpendWindow) => void;
  /** Persist a dashboard's view controls (date range, lens, metric, …) so they
   *  survive the tab unmount that happens on navigate-away. Keyed by dashboard. */
  setDashboardControl: (dashboard: string, patch: Record<string, unknown>) => void;
  setSidebarAdFilters: (filters: AdFiltersState | ((prev: AdFiltersState) => AdFiltersState)) => void;
  setWelcomeFlowPending: (pending: boolean) => void;
  setWelcomeMessageSentAt: (timestamp: number | null) => void;
  setAdSort: (key: AdSortKey) => void;
  setAdsetSort: (key: AdsetSortKey) => void;
  setSidebarPanelWidth: (width: number) => void;
  setChatbotPanelWidth: (width: number) => void;
  setLayoutMode: (mode: LayoutMode) => void;
  setGalleryDense: (dense: boolean) => void;
  setAgenticRailWidth: (width: number) => void;
  setAgenticArtifactsWidth: (width: number) => void;
  setSelectedAgentKey: (key: string | null) => void;
  setTaskFocusKey: (key: string | null) => void;
  revealInSidebar: (reveal: SidebarReveal) => void;
  clearSidebarReveal: () => void;
  setFocusedTreeEntity: (entity: { entityType: TabEntityType; entityId: string } | null) => void;
  setNudgesInitializing: (initializing: boolean) => void;
};

export interface SidebarReveal {
  entityType: TabEntityType;
  entityId: string;
  platform?: string;
  tabKey?: string;
}

interface AppState {
  isSidebarCollapsed: boolean;
  pageTitle: string;
  loading: boolean;
  error: string | null;
  companyName: string;
  companyDisplayName: string;
  isDefaultCompany: boolean;
  isConnectionLoading: boolean;
  isLeonardoOpen: boolean;
  isLeonardoNewOpen: boolean;
  // Whether the post-onboarding paywall (SubscriptionRequiredModal) is open.
  // Lifted to the store so announcement components can gate on it without
  // needing to live inside ProtectedLayout.
  isSubscriptionModalOpen: boolean;
  // Modal was opened deliberately (openCheckout), not by the automatic paywall.
  checkoutRequested: boolean;
  leonardoInitialMessage: string | null;
  mockChatMode: boolean;
  tabs: TabPane[];
  activeTabKey: string | null;
  /**
   * What the center shows when no entity tab is focused: the Home feed or the
   * account-enabled Executive tab. Persisted; a disabled account always resolves
   * to Home (`resolveCenterView`).
   */
  centerView: CenterView;
  selectedTab: string | null;
  reportsTreeExpandedKeys: React.Key[];
  assetTreeExpandedKeys: React.Key[];
  scheduledWorkTreeExpandedKeys: React.Key[];
  // Current drill-down location in the unified sidebar navigator (see setSidebarNavPath).
  sidebarNavPath: string[];
  metricsWindow: SpendWindow;
  /** Per-dashboard persisted view controls, e.g. { creative: { dates }, performance: { dates, attribution } }. */
  dashboardControls: Record<string, Record<string, unknown>>;
  // Tracks if the post-onboarding welcome flow is pending (survives refresh, per-workspace)
  welcomeFlowPending: boolean;
  // Timestamp when the welcome message was sent (persisted to prevent duplicate sends on refresh)
  welcomeMessageSentAt: number | null;
  adSort: AdSortKey;
  adsetSort: AdsetSortKey;
  sidebarPanelWidth: number;
  chatbotPanelWidth: number;
  // Home layout mode (IDE three-pane vs Cursor-style agentic). Persisted.
  // `layoutModeExplicit` flips to true the first time the user (typically staff
  // via Settings → Internal) flips the switch. Until then, `layoutMode` is the
  // default placeholder and the `agent-workspace-enabled` PostHog flag drives
  // the effective render. Once explicit, `layoutMode` overrides the flag in
  // either direction.
  layoutMode: LayoutMode;
  layoutModeExplicit: boolean;
  // Gallery dashboard card density: true packs more, smaller cards per row. Persisted.
  galleryDense: boolean;
  // Agentic-layout pane geometry — kept SEPARATE from the IDE
  // sidebar/chatbot widths so resizing one mode never mutates the other.
  agenticRailWidth: number;
  agenticArtifactsWidth: number;
  // The agent currently focused in the agentic workspace. Transient runtime
  // state — NOT persisted (a selection must never leak into IDE mode or
  // survive a reload). Special keys: '__new__' opens the new-task composer.
  selectedAgentKey: string | null;
  // When set, the IDE home CenterPanel shows the task-focus view (agents
  // rail + workspace) instead of the feed. Clicking a "needs attention" card
  // on the home feed sets this; the back button clears it. Transient.
  taskFocusKey: string | null;
  sidebarAdFilters: AdFiltersState;
  pendingSidebarReveal: SidebarReveal | null;
  // The ad entity the center panel is showing when it is NOT itself a tab —
  // a campaign drilled into in place from the Campaign Timeline dashboard. Lets
  // the sidebar tree mark and reveal that node even though the active tab is the
  // dashboard, not the campaign. Transient.
  focusedTreeEntity: { entityType: TabEntityType; entityId: string } | null;
  nudgesInitializing: boolean;
}

export type AppStore = AppState & AppActions;

const STORE_NAME = 'app';
export const DEFAULT_SIDEBAR_PANEL_WIDTH = 320;
export const DEFAULT_CHATBOT_PANEL_WIDTH = 450;
export const MIN_CHATBOT_PANEL_WIDTH = 300;
// Agentic layout pane defaults (independent of the IDE pane widths above).
export const DEFAULT_AGENTIC_RAIL_WIDTH = 320;
export const DEFAULT_AGENTIC_ARTIFACTS_WIDTH = 380;
export const MIN_AGENTIC_RAIL_WIDTH = 260;
export const MIN_AGENTIC_ARTIFACTS_WIDTH = 280;

/** Insert a newly opened tab as THE preview tab: replaces the existing preview in place, otherwise appends.
 *
 * Preview semantics earn their keep when you are BROWSING a list — clicking down fifty
 * reports or two hundred ads should not leave fifty tabs behind. A dashboard is not a list
 * item: there are three of them, opening one is a decision rather than a look, and the
 * reason to open a second is almost always to hold it beside the first. So a dashboard
 * arrives permanent, and stops evicting whatever you already had open.
 */
const insertAsPreview = (tabs: TabPane[], tab: TabPane): TabPane[] => {
  if (isDashboardTab(tab.entityType)) return [...tabs, { ...tab, preview: false }];
  const incoming = { ...tab, preview: true };
  const previewIndex = tabs.findIndex((t) => t.preview);
  return previewIndex === -1
    ? [...tabs, incoming]
    : tabs.map((t, i) => (i === previewIndex ? incoming : t));
};

const normalizeChatbotPanelWidth = (width: number): number => {
  if (!Number.isFinite(width)) {
    return DEFAULT_CHATBOT_PANEL_WIDTH;
  }
  return Math.max(MIN_CHATBOT_PANEL_WIDTH, Math.round(width));
};

const initialState: AppState = {
  isSidebarCollapsed: false, // Default to open on larger screens
  pageTitle: '',
  loading: false,
  error: null,
  companyName: '',
  companyDisplayName: '',
  isDefaultCompany: false,
  isConnectionLoading: false,
  isLeonardoOpen: false,
  isLeonardoNewOpen: false,
  isSubscriptionModalOpen: false,
  checkoutRequested: false,
  leonardoInitialMessage: null,
  mockChatMode: true, // Set to true for testing
  tabs: [],
  activeTabKey: null,
  centerView: 'home',
  selectedTab: null,
  reportsTreeExpandedKeys: [],
  assetTreeExpandedKeys: ['0-0-0'], // Assets accordion open by default for new users
  scheduledWorkTreeExpandedKeys: [],
  sidebarNavPath: [],
  metricsWindow: { type: 'all' },
  dashboardControls: {},
  welcomeFlowPending: false,
  welcomeMessageSentAt: null,
  adSort: 'name-asc',
  adsetSort: 'name-asc',
  sidebarPanelWidth: DEFAULT_SIDEBAR_PANEL_WIDTH,
  chatbotPanelWidth: DEFAULT_CHATBOT_PANEL_WIDTH,
  layoutMode: 'ide',
  layoutModeExplicit: false,
  galleryDense: false,
  agenticRailWidth: DEFAULT_AGENTIC_RAIL_WIDTH,
  agenticArtifactsWidth: DEFAULT_AGENTIC_ARTIFACTS_WIDTH,
  selectedAgentKey: null,
  taskFocusKey: null,
  sidebarAdFilters: {
    statusFilter: AdStatus.ACTIVE,
    mediaTypeFilter: 'all',
    sortOrder: 'newest',
    spendWindow: { type: 'all' },
    accountFilter: 'all',
  },
  pendingSidebarReveal: null,
  focusedTreeEntity: null,
  nudgesInitializing: true,
};

const appStoreStorage = createJSONStorage<Pick<
  AppState,
  | 'isSidebarCollapsed'
  | 'tabs'
  | 'activeTabKey'
  | 'centerView'
  | 'reportsTreeExpandedKeys'
  | 'assetTreeExpandedKeys'
  | 'scheduledWorkTreeExpandedKeys'
  | 'metricsWindow'
  | 'dashboardControls'
  | 'welcomeFlowPending'
  | 'welcomeMessageSentAt'
  | 'adSort'
  | 'adsetSort'
  | 'sidebarPanelWidth'
  | 'chatbotPanelWidth'
  | 'layoutMode'
  | 'galleryDense'
  | 'agenticRailWidth'
  | 'agenticArtifactsWidth'
  | 'sidebarAdFilters'
>>(() => ({
  getItem: (_name: string) => localStorage.getItem(getAppStorePersistKey(STORE_NAME)),
  setItem: (_name: string, value: string) => localStorage.setItem(getAppStorePersistKey(STORE_NAME), value),
  removeItem: (_name: string) => localStorage.removeItem(getAppStorePersistKey(STORE_NAME)),
}));

export const useAppStore = create<AppStore>()(
  persist(
    subscribeWithSelector((set, get) => ({
      ...initialState,

      setIsConnectionLoading: (isConnectionLoading) =>
        set({ isConnectionLoading }),
      setSidebarCollapsed: (collapsed) => set({ isSidebarCollapsed: collapsed }),
      setPageTitle: (pageTitle: string) => set({ pageTitle }),
      setIsLeonardoOpen: (isLeonardoOpen) => set({ isLeonardoOpen }),
      setIsLeonardoNewOpen: (isLeonardoNewOpen) => set({ isLeonardoNewOpen }),
      setIsSubscriptionModalOpen: (isSubscriptionModalOpen) =>
        set(isSubscriptionModalOpen
          ? { isSubscriptionModalOpen }
          : { isSubscriptionModalOpen, checkoutRequested: false }),
      openCheckout: () => set({ isSubscriptionModalOpen: true, checkoutRequested: true }),
      setLeonardoInitialMessage: (message) => set({ leonardoInitialMessage: message }),
      openLeonardoWithMessage: (message) => {
        // Use New Leonardo as the primary system
        // For now, just open New Leonardo (initial message support can be added later)
        set({ isLeonardoNewOpen: true, leonardoInitialMessage: message });
      },
      setMockChatMode: (enabled) => set({ mockChatMode: enabled }),
      setCompanyName: (companyName: string) => set({ companyName }),
      setCompanyDisplayName: (companyDisplayName: string) => set({ companyDisplayName }),
      getCompanyName: () => {
        const defaultCompanyFor = ['localhost', '127.0.0.1'];
        const { companyName } = get();
        const companyNameFromUrl = getCompanyFromUrl();
        let currentCompanyName = '';

        if (defaultCompanyFor.includes(companyNameFromUrl)) {
          currentCompanyName = companyName || DEFAULT_BRANDING_COMPANY_NAME;
        } else if (
          companyNameFromUrl &&
          companyNameFromUrl !== DEFAULT_BRANDING_COMPANY_NAME
        ) {
          currentCompanyName = companyNameFromUrl;
        } else if (
          companyName &&
          companyNameFromUrl === DEFAULT_BRANDING_COMPANY_NAME
        ) {
          currentCompanyName = companyName;
        } else {
          currentCompanyName = DEFAULT_BRANDING_COMPANY_NAME;
        }

        // Ensure we never return empty string
        const finalCompanyName = currentCompanyName || DEFAULT_BRANDING_COMPANY_NAME;

        return finalCompanyName;
      },
      getSurveyValues: () => {
        try {
          const surveyValues = LocalStorageService.getItem(
            LocalStorageKey.SURVEY_FORM_VALUES,
          ) as string;
          return surveyValues ? JSON.parse(surveyValues) : null;
        } catch (error) {
          console.error('Error getting survey values', error);
          return null;
        }
      },
      setSurveyValues: (surveyValues: any) => {
        LocalStorageService.setItem(
          LocalStorageKey.SURVEY_FORM_VALUES,
          JSON.stringify(surveyValues),
        );
      },
      addTab: (tab: TabPane) => {
        // A chip, link or recent saved before the Analytics dashboard's rename opens it under its
        // current type, id and key.
        if ((tab.entityType as string) === LEGACY_ANALYTICS_TYPE) {
          tab = { ...tab, entityType: 'analyticsDashboard', entityId: 'analytics', key: canonicalTabKey(tab.key) };
        }
        // Dashboard tabs show the canonical short label no matter which path
        // opened them — chat chips and drags carry the longer artifact name.
        const dashboardType = asDashboardEntityType(tab.entityType);
        if (dashboardType) tab = { ...tab, label: DASHBOARD_TAB_LABEL[dashboardType] };
        const { tabs } = get();
        const existingTab = tabs.find((t) => t.key === tab.key);
        if (existingTab) {
          // When re-selecting a campaign tab directly, clear any drill-down
          if (existingTab.entityType === 'campaign' && existingTab.metadata?.drillDown) {
            const updatedTabs = tabs.map((t) =>
              t.key === tab.key ? { ...t, metadata: { ...t.metadata, drillDown: null } } : t
            );
            set({ tabs: updatedTabs, activeTabKey: tab.key });
          } else if (dashboardType && existingTab.label !== tab.label) {
            // Persisted tabs opened before this normalization keep their old
            // label — refresh it on re-open.
            const updatedTabs = tabs.map((t) =>
              t.key === tab.key ? { ...t, label: tab.label } : t
            );
            set({ tabs: updatedTabs, activeTabKey: tab.key });
          } else {
            set({ activeTabKey: tab.key });
          }
        } else {
          set({ tabs: insertAsPreview(tabs, tab), activeTabKey: tab.key });
        }

        // Track the visit so the artifact surfaces in the @-picker's recents.
        // Every tab open (campaign/adset/ad/report/asset/scheduledWork) flows
        // through here — one hook covers them all. We skip
        // scheduledWorkExecution since it has no artifact counterpart.
        if (
          tab.entityType !== 'scheduledWorkExecution' &&
          !isDashboardTab(tab.entityType) &&
          tab.entityId
        ) {
          // Carry the asset's thumbnail into recents so the @-picker's Recent
          // rows render the same preview as the Media folder. Without this the
          // rows fall back to a generic icon (asset artifacts need `thumbnail`).
          // Tabs only ever carry entityType 'asset' (the file/asset split lives
          // at the Artifact level, not the tab level).
          const isAssetLike = tab.entityType === 'asset';
          useRecentArtifactsStore.getState().markInteracted({
            type: tab.entityType as ArtifactType,
            id: tab.entityId,
            name: tab.entityName ?? tab.label,
            platform: typeof tab.metadata?.platform === 'string' ? tab.metadata.platform : undefined,
            groundedBy: 'user_input',
            ...(isAssetLike && {
              thumbnail: tab.metadata?.s3_path_to_thumbnail || tab.metadata?.url || undefined,
              assetType: typeof tab.metadata?.type === 'string' ? tab.metadata.type : undefined,
            }),
          });
        }

        // Auto-reveal reports in the sidebar so the parent folder chain
        // opens to expose the bolded entry. Covers every path that opens a
        // report tab — Leo creation, chat artifact chips, report-body
        // artifact chips, deep-link URLs — without each call site needing
        // to remember to reveal separately (#3844). ReportsTree's
        // pendingReveal effect handles the actual folder walking.
        if (tab.entityType === 'report' && tab.entityId) {
          get().revealInSidebar({ entityType: 'report', entityId: tab.entityId });
        }
        // Same for assets — the drill-down navigator walks to the asset's
        // folder and scrolls it into view (replaces the old expanded-key hint).
        if (tab.entityType === 'asset' && tab.entityId) {
          get().revealInSidebar({ entityType: 'asset', entityId: tab.entityId });
        }
      },
      pinTab: (key: string) => {
        const { tabs } = get();
        if (!tabs.find((t) => t.key === key)?.preview) return;
        set({ tabs: tabs.map((t) => (t.key === key ? { ...t, preview: false } : t)) });
      },
      drillDownInTab: (tabKey: string, drillDown: DrillDown | null) => {
        const { tabs } = get();
        const updatedTabs = tabs.map((tab) => {
          if (tab.key === tabKey) {
            return { ...tab, metadata: { ...tab.metadata, drillDown } };
          }
          return tab;
        });
        set({ tabs: updatedTabs, activeTabKey: tabKey });
      },
      navigateToEntity: (opts) => {
        const { entityType, entityId, entityName, platform, parentCampaignId, parentCampaignName, parentAdsetId, parentAdsetName, metadata } = opts;

        // Campaign-level: open as a regular tab — addTab handles the
        // markInteracted write itself, so we don't duplicate it here.
        if (entityType === 'campaign' || !AD_PLATFORMS.has(platform)) {
          get().addTab({ key: `${entityType}-${entityId}`, label: entityName, entityType, entityId, entityName, metadata: { ...metadata, platform } });
          return;
        }

        // Adset/ad drill-down: this branch bypasses addTab() to mutate state
        // in one set(), so we mark the interaction directly. Without this
        // hook those visits would never reach the recents store.
        if (entityType !== 'scheduledWorkExecution' && entityId) {
          useRecentArtifactsStore.getState().markInteracted({
            type: entityType as ArtifactType,
            id: entityId,
            name: entityName,
            platform: typeof platform === 'string' ? platform : undefined,
            groundedBy: 'user_input',
          });
        }

        // Adset or ad: open the parent campaign tab with drill-down in a single set()
        if (parentCampaignId && parentCampaignName) {
          const campaignTabKey = `campaign-${parentCampaignId}`;
          const drillDown: DrillDown = { entityType: entityType as 'adset' | 'ad', entityId, entityName };
          if (entityType === 'ad' && parentAdsetId) {
            drillDown.parentAdsetId = parentAdsetId;
            drillDown.parentAdsetName = parentAdsetName;
          }

          const { tabs } = get();
          const existingTab = tabs.find((t) => t.key === campaignTabKey);
          if (existingTab) {
            const updatedTabs = tabs.map((t) =>
              t.key === campaignTabKey ? { ...t, metadata: { ...t.metadata, drillDown } } : t
            );
            set({ tabs: updatedTabs, activeTabKey: campaignTabKey });
          } else {
            const newTab: TabPane = { key: campaignTabKey, label: parentCampaignName, entityType: 'campaign', entityId: parentCampaignId, entityName: parentCampaignName, metadata: { ...metadata, platform, drillDown } };
            set({ tabs: insertAsPreview(tabs, newTab), activeTabKey: campaignTabKey });
          }
        } else {
          // Fallback: no parent info available, open as standalone tab
          get().addTab({ key: `${entityType}-${entityId}`, label: entityName, entityType, entityId, entityName, metadata: { ...metadata, platform } });
        }
      },
      updateTab: (key: string, updates: Partial<Pick<TabPane, 'label' | 'entityName' | 'metadata' | 'adSort' | 'metricGroup'>>) => {
        const { tabs } = get();
        const updatedTabs = tabs.map((tab) => {
          if (tab.key === key) {
            return {
              ...tab,
              ...updates,
              metadata: updates.metadata ? { ...tab.metadata, ...updates.metadata } : tab.metadata,
            };
          }
          return tab;
        });
        set({ tabs: updatedTabs });
      },
      removeTab: (key: string) => {
        const { tabs, activeTabKey } = get();
        const targetIndex = tabs.findIndex((pane) => pane.key === key);
        if (targetIndex === -1) return;

        const newPanes = tabs.filter((pane) => pane.key !== key);

        let newActiveKey: string | null = activeTabKey;
        if (key === activeTabKey) {
          if (newPanes.length > 0) {
            // Switch to adjacent tab
            const nextIndex = targetIndex >= newPanes.length ? newPanes.length - 1 : targetIndex;
            newActiveKey = newPanes[nextIndex].key;
          } else {
            // No tabs left -- go home
            newActiveKey = null;
          }
        }

        // Safety: if no tabs remain, always go home regardless of prior activeTabKey.
        // Guards against edge cases where onChange fires during removal and sets a stale key.
        if (newPanes.length === 0) {
          newActiveKey = null;
        }

        set({ tabs: newPanes, activeTabKey: newActiveKey });
      },
      clearAllTabs: () => {
        set({ tabs: [], activeTabKey: null });
      },
      resetStore: () => {
        set({
          ...initialState,
          // Preserve responsive sidebar state based on screen size
          isSidebarCollapsed: typeof window !== 'undefined' && window.innerWidth < 640 ? true : initialState.isSidebarCollapsed,
        });
      },
      goHome: () => {
        set({ activeTabKey: null, taskFocusKey: null, centerView: 'home' });
      },
      goExecutive: () => {
        set({ activeTabKey: null, taskFocusKey: null, centerView: 'executive' });
      },
      setActiveTab: (key: string) => {
        set({ activeTabKey: key });
      },
      setSelectedTab: (key: string | null) => {
        set({ selectedTab: key });
      },
      setReportsTreeExpandedKeys: (keys: React.Key[]) => {
        set({ reportsTreeExpandedKeys: keys });
      },
      setAssetTreeExpandedKeys: (keys: React.Key[]) => {
        set({ assetTreeExpandedKeys: keys });
      },
      setScheduledWorkTreeExpandedKeys: (keys: React.Key[]) => {
        set({ scheduledWorkTreeExpandedKeys: keys });
      },
      collapseAllSidebarTrees: () => {
        set({
          reportsTreeExpandedKeys: [],
          assetTreeExpandedKeys: [],
          scheduledWorkTreeExpandedKeys: [],
        });
      },
      expandSidebarTree: (tree: 'reports' | 'assets' | 'scheduledWork', keys: React.Key[]) => {
        const state = get();
        const merge = (existing: React.Key[]) => Array.from(new Set([...existing, ...keys]));
        // Remove root keys from other trees so they collapse, but keep subtree keys for when they reopen
        const collapseOther = (existing: React.Key[]) => existing.filter((k) => k !== '0-0-0');
        set({
          reportsTreeExpandedKeys: tree === 'reports' ? merge(state.reportsTreeExpandedKeys) : collapseOther(state.reportsTreeExpandedKeys),
          assetTreeExpandedKeys: tree === 'assets' ? merge(state.assetTreeExpandedKeys) : collapseOther(state.assetTreeExpandedKeys),
          scheduledWorkTreeExpandedKeys: tree === 'scheduledWork' ? merge(state.scheduledWorkTreeExpandedKeys) : collapseOther(state.scheduledWorkTreeExpandedKeys),
        });
      },
      setSidebarNavPath: (path: string[]) => {
        set({ sidebarNavPath: path });
      },
      setMetricsWindow: (window: SpendWindow) => {
        set({ metricsWindow: window });
      },
      setDashboardControl: (dashboard, patch) => {
        set((s) => ({
          dashboardControls: {
            ...s.dashboardControls,
            [dashboard]: { ...(s.dashboardControls[dashboard] || {}), ...patch },
          },
        }));
      },
      setSidebarAdFilters: (filters: AdFiltersState | ((prev: AdFiltersState) => AdFiltersState)) => {
        set((state) => ({
          sidebarAdFilters:
            typeof filters === 'function' ? filters(state.sidebarAdFilters) : filters,
        }));
      },
      setWelcomeFlowPending: (pending: boolean) => {
        set({ welcomeFlowPending: pending });
      },
      setWelcomeMessageSentAt: (timestamp: number | null) => {
        set({ welcomeMessageSentAt: timestamp });
      },
      setAdSort: (adSort: AdSortKey) => set({ adSort }),
      setAdsetSort: (adsetSort: AdsetSortKey) => set({ adsetSort }),
      setSidebarPanelWidth: (sidebarPanelWidth: number) => {
        const rounded = Math.round(sidebarPanelWidth);
        if (get().sidebarPanelWidth === rounded) return;
        set({ sidebarPanelWidth: rounded });
      },
      setChatbotPanelWidth: (chatbotPanelWidth: number) => {
        const normalizedWidth = normalizeChatbotPanelWidth(chatbotPanelWidth);
        if (get().chatbotPanelWidth === normalizedWidth) {
          return;
        }
        set({ chatbotPanelWidth: normalizedWidth });
      },
      setLayoutMode: (layoutMode: LayoutMode) => {
        const state = get();
        if (state.layoutMode === layoutMode && state.layoutModeExplicit) return;
        set({ layoutMode, layoutModeExplicit: true });
      },
      setGalleryDense: (galleryDense: boolean) => set({ galleryDense }),
      setAgenticRailWidth: (agenticRailWidth: number) => {
        const rounded = Math.round(agenticRailWidth);
        if (get().agenticRailWidth === rounded) return;
        set({ agenticRailWidth: rounded });
      },
      setAgenticArtifactsWidth: (agenticArtifactsWidth: number) => {
        const rounded = Math.round(agenticArtifactsWidth);
        if (get().agenticArtifactsWidth === rounded) return;
        set({ agenticArtifactsWidth: rounded });
      },
      setSelectedAgentKey: (selectedAgentKey: string | null) => {
        if (get().selectedAgentKey === selectedAgentKey) return;
        set({ selectedAgentKey });
      },
      setTaskFocusKey: (taskFocusKey: string | null) => {
        if (get().taskFocusKey === taskFocusKey) return;
        set({ taskFocusKey, selectedAgentKey: taskFocusKey });
      },
      revealInSidebar: (reveal: SidebarReveal) => {
        set({ pendingSidebarReveal: reveal });
      },
      clearSidebarReveal: () => {
        set({ pendingSidebarReveal: null });
      },
      setFocusedTreeEntity: (entity) => {
        set({ focusedTreeEntity: entity });
      },
      setNudgesInitializing: (initializing: boolean) => {
        set({ nudgesInitializing: initializing });
      },
    })),
    {
      name: STORE_NAME,
      storage: appStoreStorage,
      version: 4,
      migrate: (persistedState: any, version: number) => {
        if (version < 4) {
          // The internal warehouse Overview dashboard was retired: its saved tabs and view controls go.
          persistedState = dropRetiredDashboards(persistedState);
        }
        if (version < 3) {
          // The Performance tab became the Analytics dashboard: saved tabs move to its new type and key.
          persistedState = {
            ...persistedState,
            tabs: (persistedState.tabs || []).map((tab: any) =>
              tab.entityType === LEGACY_ANALYTICS_TYPE
                ? { ...tab, entityType: 'analyticsDashboard', entityId: 'analytics', key: canonicalTabKey(tab.key) }
                : tab,
            ),
            activeTabKey: persistedState.activeTabKey && canonicalTabKey(persistedState.activeTabKey),
          };
        }
        if (version < 1) {
          // Drop ad-platform adset/ad tabs — they're now drill-downs inside campaign tabs
          const tabs = (persistedState.tabs || []).filter((tab: any) => {
            if ((tab.entityType === 'adset' || tab.entityType === 'ad') && tab.metadata?.platform && AD_PLATFORMS.has(tab.metadata.platform)) {
              return false;
            }
            return true;
          });
          // If the active tab was removed, reset to home
          const activeTabKey = tabs.some((t: any) => t.key === persistedState.activeTabKey)
            ? persistedState.activeTabKey
            : null;
          persistedState = { ...persistedState, tabs, activeTabKey };
        }
        if (version < 2) {
          // Switch defaults: all-time metrics + Live status filter
          persistedState.metricsWindow = { type: 'all' };
          persistedState.sidebarAdFilters = {
            ...(persistedState.sidebarAdFilters || {}),
            statusFilter: AdStatus.ACTIVE,
            spendWindow: { type: 'all' },
          };
        }
        return persistedState;
      },
      partialize: (state) => ({
        isSidebarCollapsed: state.isSidebarCollapsed,
        // Preview tabs are momentary — only pinned tabs survive a reload.
        tabs: state.tabs.filter((t) => !t.preview),
        activeTabKey: state.tabs.some((t) => t.key === state.activeTabKey && !t.preview)
          ? state.activeTabKey
          : null,
        centerView: state.centerView,
        // Persist the work-focus modal selection so an OAuth round-trip from an
        // inline connect card (ConnectCard) lands the user back on the same card.
        taskFocusKey: state.taskFocusKey,
        selectedAgentKey: state.selectedAgentKey,
        reportsTreeExpandedKeys: state.reportsTreeExpandedKeys,
        assetTreeExpandedKeys: state.assetTreeExpandedKeys,
        scheduledWorkTreeExpandedKeys: state.scheduledWorkTreeExpandedKeys,
        sidebarNavPath: state.sidebarNavPath,
        metricsWindow: state.metricsWindow,
        dashboardControls: state.dashboardControls,
        welcomeFlowPending: state.welcomeFlowPending,
        welcomeMessageSentAt: state.welcomeMessageSentAt,
        adSort: state.adSort,
        adsetSort: state.adsetSort,
        sidebarPanelWidth: state.sidebarPanelWidth,
        chatbotPanelWidth: state.chatbotPanelWidth,
        layoutMode: state.layoutMode,
        layoutModeExplicit: state.layoutModeExplicit,
        galleryDense: state.galleryDense,
        agenticRailWidth: state.agenticRailWidth,
        agenticArtifactsWidth: state.agenticArtifactsWidth,
        sidebarAdFilters: state.sidebarAdFilters,
      }),
    },
  ),
);

// Selectors for optimized subscriptions (implementations must be named hooks for eslint-plugin-react-hooks)
function useAppSelectorLoading() {
  return useAppStore((state) => state.loading);
}
function useAppSelectorError() {
  return useAppStore((state) => state.error);
}
function useAppSelectorIsSidebarCollapsed() {
  return useAppStore((state) => state.isSidebarCollapsed);
}
function useAppSelectorPageTitle() {
  return useAppStore((state) => state.pageTitle);
}
function useAppSelectorIsConnectionLoading() {
  return useAppStore((state) => state.isConnectionLoading);
}
function useAppSelectorIsLeonardoOpen() {
  return useAppStore((state) => state.isLeonardoOpen);
}
function useAppSelectorIsLeonardoNewOpen() {
  return useAppStore((state) => state.isLeonardoNewOpen);
}
function useAppSelectorIsSubscriptionModalOpen() {
  return useAppStore((state) => state.isSubscriptionModalOpen);
}
function useAppSelectorLeonardoInitialMessage() {
  return useAppStore((state) => state.leonardoInitialMessage);
}
function useAppSelectorMockChatMode() {
  return useAppStore((state) => state.mockChatMode);
}
function useAppSelectorTabs() {
  return useAppStore((state) => state.tabs);
}
function useAppSelectorActiveTabKey() {
  return useAppStore((state) => state.activeTabKey);
}
function useAppSelectorCenterView() {
  return useAppStore((state) => state.centerView);
}
function useAppSelectorReportsTreeExpandedKeys() {
  return useAppStore((state) => state.reportsTreeExpandedKeys);
}
function useAppSelectorAssetTreeExpandedKeys() {
  return useAppStore((state) => state.assetTreeExpandedKeys);
}
function useAppSelectorScheduledWorkTreeExpandedKeys() {
  return useAppStore((state) => state.scheduledWorkTreeExpandedKeys);
}
function useAppSelectorSidebarNavPath() {
  return useAppStore((state) => state.sidebarNavPath);
}
function useAppSelectorMetricsWindow() {
  return useAppStore((state) => state.metricsWindow);
}
function useAppSelectorWelcomeFlowPending() {
  return useAppStore((state) => state.welcomeFlowPending);
}
function useAppSelectorWelcomeMessageSentAt() {
  return useAppStore((state) => state.welcomeMessageSentAt);
}
function useAppSelectorAdSort() {
  return useAppStore((state) => state.adSort);
}
function useAppSelectorAdsetSort() {
  return useAppStore((state) => state.adsetSort);
}
function useAppSelectorChatbotPanelWidth() {
  return useAppStore((state) => state.chatbotPanelWidth);
}
function useAppSelectorLayoutMode() {
  return useAppStore((state) => state.layoutMode);
}
function useAppSelectorLayoutModeExplicit() {
  return useAppStore((state) => state.layoutModeExplicit);
}
function useAppSelectorGalleryDense() {
  return useAppStore((state) => state.galleryDense);
}
function useAppSelectorAgenticRailWidth() {
  return useAppStore((state) => state.agenticRailWidth);
}
function useAppSelectorAgenticArtifactsWidth() {
  return useAppStore((state) => state.agenticArtifactsWidth);
}
function useAppSelectorSelectedAgentKey() {
  return useAppStore((state) => state.selectedAgentKey);
}
function useAppSelectorTaskFocusKey() {
  return useAppStore((state) => state.taskFocusKey);
}
function useAppSelectorSidebarAdFilters() {
  return useAppStore((state) => state.sidebarAdFilters);
}
function useAppSelectorPendingSidebarReveal() {
  return useAppStore((state) => state.pendingSidebarReveal);
}
function useAppSelectorNudgesInitializing() {
  return useAppStore((state) => state.nudgesInitializing);
}

export const useAppSelectors = {
  loading: useAppSelectorLoading,
  error: useAppSelectorError,
  isSidebarCollapsed: useAppSelectorIsSidebarCollapsed,
  pageTitle: useAppSelectorPageTitle,
  isConnectionLoading: useAppSelectorIsConnectionLoading,
  isLeonardoOpen: useAppSelectorIsLeonardoOpen,
  isLeonardoNewOpen: useAppSelectorIsLeonardoNewOpen,
  isSubscriptionModalOpen: useAppSelectorIsSubscriptionModalOpen,
  leonardoInitialMessage: useAppSelectorLeonardoInitialMessage,
  mockChatMode: useAppSelectorMockChatMode,
  tabs: useAppSelectorTabs,
  activeTabKey: useAppSelectorActiveTabKey,
  centerView: useAppSelectorCenterView,
  reportsTreeExpandedKeys: useAppSelectorReportsTreeExpandedKeys,
  assetTreeExpandedKeys: useAppSelectorAssetTreeExpandedKeys,
  scheduledWorkTreeExpandedKeys: useAppSelectorScheduledWorkTreeExpandedKeys,
  sidebarNavPath: useAppSelectorSidebarNavPath,
  metricsWindow: useAppSelectorMetricsWindow,
  welcomeFlowPending: useAppSelectorWelcomeFlowPending,
  welcomeMessageSentAt: useAppSelectorWelcomeMessageSentAt,
  adSort: useAppSelectorAdSort,
  adsetSort: useAppSelectorAdsetSort,
  chatbotPanelWidth: useAppSelectorChatbotPanelWidth,
  layoutMode: useAppSelectorLayoutMode,
  layoutModeExplicit: useAppSelectorLayoutModeExplicit,
  galleryDense: useAppSelectorGalleryDense,
  agenticRailWidth: useAppSelectorAgenticRailWidth,
  agenticArtifactsWidth: useAppSelectorAgenticArtifactsWidth,
  selectedAgentKey: useAppSelectorSelectedAgentKey,
  taskFocusKey: useAppSelectorTaskFocusKey,
  sidebarAdFilters: useAppSelectorSidebarAdFilters,
  pendingSidebarReveal: useAppSelectorPendingSidebarReveal,
  nudgesInitializing: useAppSelectorNudgesInitializing,
};
